import { useEffect, useCallback, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import Editor from 'react-simple-code-editor';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, TextField, FormControl, FormHelperText, Grid } from '@mui/material';
// @ts-ignore
import { highlight, languages } from 'prismjs/components/prism-core';

import { useTheme } from '@mui/material/styles';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css';

import './EmailTemplateStyle.css';

import { EmailTemplate } from '../../models';
import { useAddEmailTemplateMutation } from '../../slices/apis/emailTemplateApi';

export default function EmailTemplateNew() {
	const navigate = useNavigate();
	const theme = useTheme();
	const [addEmailTemplate, { isSuccess }] = useAddEmailTemplateMutation();

	const formik = useFormik({
		initialValues: EmailTemplate.schema.getDefault(),
		validationSchema: EmailTemplate.schema,
		onSubmit: async (values) => {
			await addEmailTemplate(values);
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (isSuccess) {
			navigate('/dashboard/admin/email-templates');
		}
	}, [navigate, isSuccess]);

	return (
		<>
			<Helmet>
				<title> New Email Template - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Grid container justifyContent="start">
				<Grid size={{ md: 6 }}>
					<Card sx={{ m: 2 }}>
						<CardContent>
							<form onSubmit={formik.handleSubmit}>
								<Grid container spacing={2}>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="name"
											name="name"
											label="Name"
											variant="standard"
											value={formik.values.name}
											onChange={formik.handleChange}
											error={formik.touched.name && Boolean(formik.errors.name)}
											helperText={formik.touched.name && formik.errors.name}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="subject"
											name="subject"
											label="Subject"
											variant="standard"
											type="subject"
											value={formik.values.subject}
											onChange={formik.handleChange}
											error={formik.touched.subject && Boolean(formik.errors.subject)}
											helperText={formik.touched.subject && formik.errors.subject}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<FormControl fullWidth error={formik.touched.bodyHtml && Boolean(formik.errors.bodyHtml)}>
											<Editor
												id="bodyHtml"
												name="bodyHtml"
												value={formik.values.bodyHtml}
												onValueChange={(val) => formik.setFieldValue('bodyHtml', val)}
												highlight={(code) => highlight(code, languages.markup)}
												padding={10}
												style={{
													fontFamily: '"Fira code", "Fira Mono", monospace',
													fontSize: 12,
													border: `1.5px ${theme.palette.grey[400]} solid`,
													borderRadius: '4px',
												}}
												textareaClassName="email-template-editor"
											/>
											<FormHelperText sx={{ ml: 0 }}>
												{formik.touched.bodyHtml && formik.errors.bodyHtml}
											</FormHelperText>
										</FormControl>
									</Grid>
									<Grid size={{ md: 4 }}>
										<Grid container justifyContent="start" spacing={4}>
											<Grid size={{ md: 6 }}>
												<Button loading={formik.isSubmitting} variant="outlined" color="primary" type="submit">
													<span>Save</span>
												</Button>
											</Grid>
											<Grid size={{ md: 6 }}>
												<Button
													color="error"
													variant="outlined"
													data-route="/dashboard/admin/email-templates"
													onClick={handleNavigate}
												>
													Cancel
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</form>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
